import _ from "underscore";
import { createElement, extend } from "../../utils/vanilla";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { handleApi } from "../../utils/error";
import { Dialog } from "./dialog";
import DriveView, { IDriveViewOptions } from "../../DriveView";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";

import templateStr from "./autoCompletionInput.tmpl";
import { DataActions } from "../../store/data/actions";

/* eslint-disable @typescript-eslint/naming-convention */
interface IInputHints {
    D: string[];
    ON: string[];
    OFF: string[];
    edit: string[];
    PC: string[];
    YM: string[];
}
/* eslint-enable @typescript-eslint/naming-convention */

interface IAutoCompletionInputOptions extends IDriveViewOptions<Backbone.Model> {
    action: string;
    maxSize?: number;
    value?: string;
    placeholder?: string;
    ariaLabel?: string;
    onChange?: (val: string) => void;
}

const constantHints: IInputHints = {
    "D": [
        "Highway closure collision",
        "Inclement weather",
        "Deferral of OFF duty",
        "Split sleeper",
        "Drop and hook",
        "Collision"
    ],
    "ON": [
        "Loading/unloading",
        "Tire/load check",
        "Brake check",
        "Completing paperwork or forms",
        "Fuel",
        "Border crossing",
        "Post trip",
        "Roadside inspection",
        "Training/orientation/recertification",
        "CTPAT security check",
        "Beginning of shift",
        "Pre-trip",
        "Customer check in/check out",
        "End of shift"
    ],
    "OFF": [
        "Break"
    ],
    "edit": [
        "Forgot to change duty status",
        "Forgot to log out",
        "Unable to log in",
        "System malfunction",
        "Forgot to log fuel time"
    ],
    "PC": [
        "Lunch",
        "Dinner",
        "Heading home",
        "Parking",
        "Personal errand"
    ],
    "YM": [
        "Changing docks",
        "Weighing vehicle/trailer",
        "Maintenance",
        "Moving trailers"
    ]
};

const html = _.template(templateStr);
const DROPDOWN_ITEM_CLASS = "dropdown_item";

export default class AutoCompletionInput extends DriveView<Backbone.Model> {
    private readonly dropdownId = "annotationDropdown";
    private readonly maxSize: number;
    private readonly onChange?: (val: string) => void;
    private readonly placeholder?: string;
    private readonly initialValue?: string;
    private readonly ariaLabel?: string;
    private readonly inputHandlerBind: (e) => void;
    private readonly onDropdownClickBind: (e) => void;
    private inputElem: HTMLInputElement;
    private hints: string[];
    private storedHints: string[];
    private dropdown: Dialog | null = null;
    private action: string;
    private translatedHints: string[];
    constructor (options: IAutoCompletionInputOptions) {
        super(options);
        this.action = options.action;
        this.storedHints = this.getCustomHints();
        this.hints = constantHints[this.action] || [];
        this.maxSize = options.maxSize || 10000;
        this.placeholder = options.placeholder;
        this.ariaLabel = options.ariaLabel;
        this.initialValue = options.value || "";
        if (options.app){
            this.app = options.app;
        }

        this.onChange = options.onChange;
        this.inputHandlerBind = (e) => this.inputHandler(e);
        this.onDropdownClickBind = (e) => this.onDropdownClick(e);
    }

    get dropdownMenu () {
        return this.dropdown;
    }

    get customHints () {
        return this.storedHints;
    }

    get firstHint (){
        return this.hints[0];
    }

    get val () {
        // inputElem is set asynchronously after render(), so fall back to the initial value until then.
        return this.inputElem ? this.inputElem.value : this.initialValue || "";
    }

    set val (value: string) {
        this.inputElem.value = value;
        this.onChange && this.onChange(value);
    }

    inputHandler (e: Event) {
        let pattern =  (e.currentTarget as HTMLInputElement).value || "";
        let matchingHints = this.findMatchingHints(this.translatedHints.concat(this.storedHints), pattern);
        this.onChange && this.onChange(this.inputElem.value);

        if (pattern.length > 0 && matchingHints.length > 0) {
            this.renderDropdown(matchingHints);
        } else {
            this.removeDropdown();
        }
    }

    clearCachedHints () {
        let user = this.app.userService.getCurrentUser();
        user && this.app?.store.dispatch(DataActions.clearInputHints(user.id));
        this.storedHints = [];
    }

    renderImpl () {
        return Promise.all([
            this.translator.translate(this.hints),
            this.display(html({ dropdownId: this.dropdownId, maxSize: this.maxSize, value: this.initialValue, placeholder: this.placeholder, ariaLabel: this.ariaLabel }))
        ])
        .then(([ translatedHints ]) => {
            this.translatedHints = translatedHints;
            this.inputElem = this.el.querySelector(".combobox__input");
            if (this.inputElem) {
                this.inputElem.addEventListener("click", this.inputHandlerBind, false);
                this.inputElem.addEventListener("input", this.inputHandlerBind, false);
            }
        })
        .catch(handleApi);
    }

    render (){
        this.renderImpl();
        return this;
    }


    cacheHint (newHint: string) {
        if (!this.isAlreadyInArray(newHint)) {
            this.storedHints.push(newHint);

            if (this.maxSize && this.storedHints.length > this.maxSize) {
                this.storedHints.shift();
            }

            let user = this.app.userService.getCurrentUser();
            user && this.app.store.dispatch(DataActions.setInputHints({[user.id]: this.storedHints}));
        }
    }

    remove () {
        this.dropdown && this.dropdown.remove();
        if (this.inputElem) {
            this.inputElem.removeEventListener("click", this.inputHandlerBind, false);
            this.inputElem.removeEventListener("input", this.inputHandlerBind, false);
        }

        return this;
    }

    setAction (action: string) {
        this.storedHints = this.getCustomHints();
        this.hints = constantHints[action] || [];
        this.action = action;

        this.remove();
        return this.render();
    }

    private readonly onDropdownClick = (e: Event) => {
        let target = e.target as HTMLElement | null;
        if (target?.classList.contains(DROPDOWN_ITEM_CLASS)) {
            dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
            let annotation = target.textContent || "";
            this.val = annotation;
            this.removeDropdown();
        }
    };

    private getCustomHints (): string[] {
        let user = this.app.userService.getCurrentUser();
        if (user && this.app?.store.getState().data.inputHints[user.id]) {
            return extend(true, [], this.app.store.getState().data.inputHints[user.id]);
        }
        return [];
    }

    private setExpandedState (state: boolean) {
        let combobox = (this.el as HTMLElement).querySelector(".combobox");
        combobox?.setAttribute("aria-expanded", state ? "true" : "false");
    }

    private removeDropdown () {
        this.dropdown?.el?.removeEventListener("click", this.onDropdownClickBind, false);
        this.dropdown?.remove();
    }

    private createDropdownItem (text: string) {
        let el = createElement("div", `${ DROPDOWN_ITEM_CLASS } noTranslate`);
        el.setAttribute("role", "listitem");
        el.setAttribute("aria-label", "");
        el.setAttribute("aria-live", "polite");
        el.innerText = text;
        return el;
    }

    private renderDropdown (hintsToRender: string[]) {
        if (!this.dropdown) {
            this.dropdown = new Dialog(
                this.dropdownId,
                "",
                this.translator,
                undefined,
                undefined,
                {
                    bindWidth: true,
                    alignToElement: this.inputElem,
                    focusOnFirstNonExitButton: false,
                    adjustable: true,
                    modal: false,
                    attributes: {
                        "role": "listbox",
                        "aria-label": this.el.querySelector(".combobox__input").getAttribute("placeholder")
                    },
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );

            this.dropdown.onRemove(() => {
                this.setExpandedState(false);
            });
        }

        this.dropdown.setMessage(hintsToRender.map(this.createDropdownItem.bind(this)));
        if (this.dropdown.isOpen()) {
            this.dropdown.resize();
        } else {
            this.dropdown.open().then(() => {
                this.setExpandedState(true);
                this.dropdown?.el?.addEventListener("click", this.onDropdownClickBind, false);
                this.dropdown?.el?.addEventListener("input", this.inputHandlerBind, false);
            }).catch(handleApi);
        }
    }

    private matchWordStart(lcHint: string, lcPattern: string) {
        // If the search pattern contains a space, OR
        // if the search pattern ends with a non-alphanumeric character,
        // we check if the hint includes the pattern as a substring.
        // This allows patterns like "trailer ", "vehicle/", "item-" to match.
        const lastCharOfPattern = lcPattern[lcPattern.length - 1];
        const patternEndsWithNonAlphanumeric = /[^a-z0-9]/i.test(lastCharOfPattern);
        if (lcPattern.includes(" ") || patternEndsWithNonAlphanumeric) {
            return lcHint.includes(lcPattern);
        } else {
            // The pattern does not contain a space and ends with an alphanumeric character.
            // Split the hint into words using any non-alphanumeric characters as delimiters.
            // Then check if any word starts with the pattern.
            // This allows "123" to match the "123" part of "Trailer 123".
            const words = lcHint.split(/[^a-z0-9]+/i).filter(word => word.length > 0);
            return words.some(word => word.startsWith(lcPattern));
        }
    }

    private findMatchingHints (hints: string[], pattern: string) {
        return hints.filter(hint => this.matchWordStart(hint.toLowerCase(), pattern.toLowerCase()));
    }

    private isAlreadyInArray (hintToCheck: string) {
        return this.hints.concat(this.storedHints).some(hint => hint.toLowerCase() === hintToCheck.toLowerCase());
    }
}